import { FC, useMemo, useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';

import { useAppContext } from '@contexts/AppContext';
import dayjs from 'dayjs';
import { sum } from 'lodash';

import { EntertainerRank, useTopEntertainersQuery } from '@queries/EntertainerQueries';

import {
  CritiqueDocumentMutationPayload,
  useCritiqueDocumentMutation,
  useTopEntertainersOptimisticUpdate,
} from '@mutations/CritiqueMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import { PostCardHeaderWrapper } from '@components/cards/PostCard/PostCard';
import Card from '@components/layout/Card/Card';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { BASIC_RATING_COLOR_RECORD } from '@components/overlays/CritiqueOverlay/CritiqueOverlayConstants';
import { PrimaryCritiqueCountChips } from '@components/overlays/CritiqueOverlay/PrimaryCritiqueCountChips';

import { EntertainerCritiquePopup } from '@screens/entertainer/EntertainerRankingsScreen/EntertainerCritiquePopup';
import { useEntertainerCritiquePopup } from '@screens/entertainer/EntertainerRankingsScreen/EntertainerCritiquePopupContext';

export const EntertainerCritiquePopupButton: FC<{ entertainerId: string }> = ({
  entertainerId: originalEntertainerId,
}) => {
  const { track } = useAnalytics();
  const { openEntertainerCritiquePopup } = useEntertainerCritiquePopup();

  return (
    <>
      <Button
        className="py-2 px-3"
        size="sm"
        roundness="rounded"
        variant="smooth"
        color="neutral"
        onClick={() => {
          track('Open Entertainer Critique Popup', {
            originalEntertainerId,
          });
          openEntertainerCritiquePopup(originalEntertainerId);
        }}
      >
        Details
      </Button>
    </>
  );
};

export const RatingActivityCard = ({
  post,
  meta = {},
  onCritique,
}: {
  post: any;
  meta?: any;
  onCritique?: () => void;
}) => {
  const { track } = useAnalytics();
  const [userPrimaryRating, setUserPrimaryRating] = useState<string>();

  const primaryRatings = useMemo<Record<string, number>>(() => {
    return Object.fromEntries(
      Object.entries(post.basicRatings).map(([key, value]: [string, number]) => [
        key,
        key === userPrimaryRating ? value + 1 : value,
      ]),
    );
  }, [post.basicRatings, userPrimaryRating]);

  const pieChartData: { key: string; value: number; color: string }[] = useMemo(() => {
    const total =
      sum(Object.values(primaryRatings)) -
        // Remove dislikes from the total unless the current user clicked dislike
        post.basicRatings.dislike ?? 0;

    return Object.entries(primaryRatings)
      .filter(([key, value]) => value && key !== 'dislike')
      .map(([key, value]: [string, number]) => ({
        key,
        value: Math.round((value / total) * 100),
        color: BASIC_RATING_COLOR_RECORD[key],
      }));
  }, [primaryRatings]);

  const { critiqueDocumentAsync } = useCritiqueDocumentMutation();
  const handleSelectPrimaryRating = async (payload: CritiqueDocumentMutationPayload) => {
    track('Rating Activity Rate Click', {
      documentId: post.documentId,
      title: post.entertainer.title,
      primaryCritique: payload.primaryCritique,
      isBoostReleased: true,
      isBoosted: post.isBoosted ?? false,
      ...meta,
    });

    setUserPrimaryRating(payload.primaryCritique);
    onCritique?.();

    await critiqueDocumentAsync({
      primaryCritique: payload.primaryCritique,
      documentType: 'entertainers',
      documentId: post.documentId,
      meta: {
        isBoostReleased: true,
        isBoosted: post.isBoosted ?? false,
        ...meta,
      },
    });
  };

  const pieChartDataIsEmpty = pieChartData.every((data) => data.value === 0);

  return (
    <Card>
      <PostCardHeaderWrapper
        wrapperTitle="Latest Ratings"
        title={post.entertainer.title ?? 'N/A'}
        createdAt={dayjs(post.createdAt).toISOString()}
        action={<EntertainerCritiquePopupButton entertainerId={post.documentId} />}
      />
      <Card.Body>
        <div className="d-flex gap-3">
          <div style={{ width: '33.3333%' }}>
            <PieChart
              rounded
              lineWidth={25}
              radius={50}
              data={pieChartData}
              paddingAngle={25}
              // label={({ dataEntry }) => `${dataEntry.value}%`}
              labelStyle={(index) => ({
                fill: pieChartData[index].color,
                fontSize: '12px',
                fontWeight: 'bold',
                fontFamily: 'Rubik',
                letterSpacing: 1,
              })}
              {...(pieChartDataIsEmpty
                ? {
                    rounded: false,
                    data: [{ key: 'empty', value: 100, color: 'rgba(241,241,241, .2)' }],
                    label: undefined,
                    labelStyle: undefined,
                    paddingAngle: 0,
                  }
                : {})}
            />
          </div>
          <div style={{ width: '66.6666%' }}>
            <div
              className="d-flex flex-column justify-content-center align-items-center h-100"
              style={{
                background: 'rgba(0,0,0,.2)',
                borderRadius: 8,
              }}
            >
              <div className="text-align-center mb-1">
                <strong>What's your take?</strong>
              </div>
              <div className="text-align-center">
                <div className="d-inline-flex">
                  <PrimaryCritiqueCountChips
                    hideCounts
                    disableOverlay
                    controlSelectedRating
                    counts={{
                      like: 0,
                      dislike: 0,
                      neutral: 0,
                    }}
                    onSubmit={handleSelectPrimaryRating}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
